<template>
  <video-in-trash
    v-if="errors.isVideoInTrash"
    :video-in-trash-data="videoInTrashData"
  >
  </video-in-trash>
  <video-not-found v-else-if="errors.videoDoesNotExist"></video-not-found>
  <page-view v-else :is-loading="isLoading">
    <template #header>
      <base-header
        :title="pageTitle"
        :subtitle="video.title"
        :isMobileBreakpoint="isMobileBreakpoint"
        :has-intro="isMobileBreakpoint ? false : true"
        @title="handleBackToListing"
        @back="handleBackToListing"
      >
        <template #info>
          <template v-if="isExternalSourceVideo">
            <v-chip-legacy
              class="external-chip ma-0 mr-3"
              dense
              @click="handleGoToLink(video.video_url)"
            >
              <v-icon left> mdi-link </v-icon>
              {{ getExternalLabelValue(video).replace('www.', '') }}
            </v-chip-legacy>
          </template>
        </template>
        <template #status>
          <!-- dataBeingReplicated -->
          <template v-if="dataBeingReplicated && !isMobileBreakpoint">
            <small id="update-changes" style="font-size: 10px">
              {{ $t('message.pages.video.publishChanges') }}
            </small>
            <v-progress-circular
              indeterminate
              :size="16"
              :width="3"
              color="brand"
            ></v-progress-circular>
          </template>
          <v-menu
            v-if="isConverting"
            content-class="converting-video-tooltip"
            open-on-hover
            :v-model="showConvertingVideoTooltip"
          >
            <template #activator="{ on, attrs }">
              <v-row v-on="on" v-bind="attrs" no-gutters>
                <base-progress-circular></base-progress-circular>
                <base-chip class="converting-chip" size="small">{{
                  $t('message.pages.videoPage.details.alert.converting.chip')
                }}</base-chip>
              </v-row>
            </template>
            <v-row
              align="center"
              align-content="center"
              justify="center"
              no-gutters
            >
              <v-col>
                <span
                  class="body-bold-smaller"
                  v-html="
                    $t(
                      'message.pages.videoPage.details.alert.converting.tooltipHeader'
                    )
                  "
                ></span>
              </v-col>
              <v-col class="ml-1">
                <base-btn
                  size="xsmall"
                  type="neutral"
                  class="converting-video-tooltip--btn"
                  @click="goToConversionArticle"
                  ><span class="body-bold-smaller">{{
                    $t(
                      'message.pages.videoPage.details.alert.converting.action'
                    )
                  }}</span
                  ><v-icon class="body-bold-smaller pl-2" size="20"
                    >mdi-open-in-new</v-icon
                  ></base-btn
                >
              </v-col>
            </v-row>
          </v-menu>
        </template>
        <template #actions>
          <span
            :data-step="1"
            :data-title="$t(introPath + 'share.title')"
            :data-intro="$t(introPath + 'share.description')"
          >
            <base-btn
              v-if="!isMobileBreakpoint"
              type="tertiary"
              @click="showEmbed(0)"
            >
              {{ $t('message.embedModal.shareVideo') }}
            </base-btn>
            <base-btn
              v-if="!isMobileBreakpoint"
              @click="showEmbed(1)"
              style="margin-left: 1rem"
              :disabled="
                isExternalSourceNoLengthVideo && loadingExternalDuration
              "
              :loading="
                isExternalSourceNoLengthVideo && loadingExternalDuration
              "
            >
              Embed
            </base-btn>
          </span>
          <v-row @click="showDots" style="cursor: pointer">
            <span
              class="dots-row"
              :class="{
                'dots-active': showMenu,
              }"
            >
              <span>
                <v-icon size="16"> panda-menu-action </v-icon>
              </span>
            </span>
          </v-row>
        </template>
      </base-header>
    </template>

    <template #content>
      <!-- content -->
      <div class="grid" :class="{ 'grid--mobile': isMobileBreakpoint }">
        <div class="grid__left-col">
          <div class="grid__preview">
            <v-sheet>
              <v-row no-gutters>
                <v-col :cols="isMobileBreakpoint ? '12' : '12'">
                  <v-row no-gutters>
                    <v-col
                      :data-step="4"
                      :data-title="$t(introPath + 'preview.title')"
                      :data-intro="$t(introPath + 'preview.description')"
                      ref="player"
                      cols="12"
                      class="pa-0"
                    >
                      <v-card
                        v-if="
                          (video.status === 'CONVERTING' ||
                            video.status === 'FAILED' ||
                            video.status === 'DRAFT') &&
                          video.playable === false
                        "
                        class="d-flex flex-column justify-center align-center"
                        height="415px"
                        max-width="740px"
                      >
                        <template v-if="video.status === 'FAILED'">
                          <v-row align="center" justify="center">
                            <span class="red--text text-subtitle-1">
                              <v-icon size="20" color="red">
                                mdi-alert-circle-outline
                              </v-icon>
                              {{ $t('message.processingError') }}
                            </span>
                          </v-row>
                        </template>
                        <template v-else-if="isConvertingTooLazy">
                          <v-row>
                            <v-col
                              cols="12"
                              class="d-flex justify-center align-center pa-0 pb-2"
                            >
                              <span class="text-center" style="font-size: 12px">
                                {{
                                  $t(
                                    'message.pages.videoEditModal.videoConvertingText2'
                                  )
                                }}
                                <br />
                                {{
                                  $t(
                                    'message.pages.videoEditModal.videoConvertingText3'
                                  )
                                }}
                                <v-progress-circular
                                  indeterminate
                                  :size="16"
                                  :width="3"
                                  color="brand"
                                ></v-progress-circular>
                              </span>
                            </v-col>
                          </v-row>
                        </template>
                        <template v-else>
                          <v-row>
                            <v-col
                              cols="12"
                              class="d-flex justify-center align-center pa-0 pb-2"
                            >
                              <span class="text-center">
                                {{
                                  video.playable
                                    ? $t(
                                        'message.pages.videoEditModal.videoConvertingText4'
                                      )
                                    : $t(
                                        'message.pages.videoEditModal.videoConvertingText'
                                      )
                                }}...
                                <v-progress-circular
                                  :size="16"
                                  :width="3"
                                  indeterminate
                                  color="brand"
                                ></v-progress-circular>
                              </span>
                            </v-col>
                          </v-row>
                        </template>
                      </v-card>
                      <v-card flat v-else>
                        <iframe-loader v-if="isIframeLoading"></iframe-loader>
                        <v-card
                          v-if="!isLandscapeOrientation"
                          style="margin: 0 auto"
                          flat
                          max-width="calc((100vh - 5rem) / 1.77778)"
                        >
                          <template v-if="konva.file && konva.img.image">
                            <div class="konva__container">
                              <v-stage
                                ref="stage"
                                class="konva__stage"
                                :config="konva.stage"
                                @mousedown="updateTransformer"
                                @touchstart="updateTransformer"
                              >
                                <v-layer ref="layer">
                                  <v-image
                                    :config="konva.img"
                                    @dragend="handleTransformEnd"
                                    @transformend="handleTransformEnd"
                                  />
                                  <v-transformer ref="transformer" />
                                </v-layer>
                              </v-stage>
                              <span
                                class="konva__player"
                                v-html="embedPage()"
                                v-if="!isExternalSourceVideo"
                              />
                              <div
                                class="konva__player"
                                :id="`pandaexternal-${video.video_external_id}`"
                                v-else
                                v-html="embedPage()"
                              ></div>
                            </div>
                          </template>
                          <span
                            v-else-if="!isExternalSourceVideo"
                            v-html="embedPage()"
                          />
                          <div
                            v-else
                            class="konva__player"
                            :id="`pandaexternal-${video.video_external_id}`"
                            v-html="embedPage()"
                          ></div>
                        </v-card>
                        <span v-else-if="errors.videoDoesNotExist">
                          <v-card
                            class="d-flex flex-column justify-center align-center"
                            flat
                            height="300px"
                            width="530px"
                          >
                            <span class="red--text">
                              <v-row align="center" justify="center">
                                <span class="red--text text-subtitle-1">
                                  <v-icon
                                    height="20"
                                    color="red"
                                    class="d-inline-block mt-n1"
                                  >
                                    mdi-alert-circle-outline
                                  </v-icon>
                                  {{ $t('message.videoDoesNotExist') }}
                                </span>
                              </v-row>
                            </span>
                          </v-card>
                        </span>
                        <template v-else>
                          <template v-if="konva.file && konva.img.image">
                            <div class="konva__container">
                              <v-stage
                                ref="stage"
                                class="konva__stage"
                                :config="konva.stage"
                                @mousedown="updateTransformer"
                                @touchstart="updateTransformer"
                              >
                                <v-layer ref="layer">
                                  <v-image
                                    :config="konva.img"
                                    @dragend="handleTransformEnd"
                                    @transformend="handleTransformEnd"
                                  />
                                  <v-transformer ref="transformer" />
                                </v-layer>
                              </v-stage>
                              <span
                                class="konva__player"
                                v-html="embedPage()"
                                v-if="!isExternalSourceVideo"
                              />
                              <div
                                class="external__player"
                                :id="`pandaexternal-${video.video_external_id}`"
                                v-html="embedPage()"
                                v-else
                              ></div>
                            </div>
                          </template>
                          <span
                            v-else-if="!isExternalSourceVideo"
                            v-html="embedPage()"
                          />
                          <div
                            class="external__player"
                            :id="`pandaexternal-${video.video_external_id}`"
                            v-html="embedPage()"
                            v-else
                          ></div>
                        </template>
                      </v-card>
                    </v-col>
                    <!-- video status handler -->
                    <v-col v-if="isConverting" cols="12" class="pa-0 mt-6">
                      <base-alert
                        type="informative"
                        :title="
                          $t(
                            'message.pages.videoPage.details.alert.converting.title'
                          )
                        "
                        :description="
                          $t(
                            'message.pages.videoPage.details.alert.converting.description'
                          )
                        "
                      >
                        <v-row no-gutters>
                          <base-btn
                            size="small"
                            type="secondary"
                            class="converting-alert-btn"
                            @click="goToConversionArticle"
                            >{{
                              $t(
                                'message.pages.videoPage.details.alert.converting.action'
                              )
                            }}<v-icon class="body-bold-smaller pl-2" size="20"
                              >mdi-open-in-new</v-icon
                            ></base-btn
                          >
                        </v-row>
                      </base-alert>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <form
                ref="downloadForm"
                method="post"
                :action="downloadUrl"
                style="display: none"
              >
                <input
                  ref="downloadInput"
                  name="Authorization"
                  :value="authToken"
                  type="hidden"
                />
              </form>
            </v-sheet>
            <v-row
              v-if="isMobileBreakpoint && !loadingVideoSettings"
              no-gutters
              align="center"
              align-content="center"
              justify="center"
              class="details--mobile"
            >
              <v-col cols="6" align-self="center">
                <base-btn @click="showDetails = true" type="tertiary"
                  >Informações</base-btn
                >
              </v-col>
              <v-col cols="6" align-self="center">
                <base-btn
                  @click="showEmbed(1)"
                  style="margin-left: 1rem"
                  :disabled="
                    isExternalSourceNoLengthVideo && loadingExternalDuration
                  "
                  :loading="
                    isExternalSourceNoLengthVideo && loadingExternalDuration
                  "
                >
                  Embed
                </base-btn>
              </v-col>
            </v-row>
          </div>
          <div class="grid__details">
            <portal to="details--mobile" :disabled="!isMobileBreakpoint">
              <video-details-loader
                v-if="PandaPlayerIsNotReady"
              ></video-details-loader>
              <v-container class="pa-6" v-else>
                <v-row
                  v-if="!isLoading"
                  class="d-flex flex-column"
                  no-gutters
                  justify="end"
                >
                  <v-col v-if="errors.failedToUpdateVideo" cols="6">
                    <v-card
                      height="25"
                      class="d-flex flex-row justify-end align-center"
                      flat
                    >
                      <span class="red--text caption align-text justify-end">
                        <v-icon color="red" size="16" class="pb-1">
                          mdi-alert-circle
                        </v-icon>
                        {{ $t('message.errors.networkError') }}
                        {{ networkErrorMsg }}
                      </span>
                    </v-card>
                  </v-col>
                </v-row>
                <v-row class="d-flex flex-column ma-0">
                  <v-row
                    no-gutters
                    :data-step="5"
                    :data-title="$t(introPath + 'details.title')"
                    :data-intro="$t(introPath + 'details.description')"
                  >
                    <!-- video-name -->
                    <v-col
                      cols="12"
                      class="container-title-description pa-0 mt-5 mb-6"
                      :class="{ hide: isLoading }"
                    >
                      <template v-if="video.status === 'BLOCKED'">
                        <span
                          v-bind="attrs"
                          v-on="on"
                          class="d-flex red--text ml-2 noIbar caption"
                        >
                          <v-icon size="18" color="red">
                            mdi-alert-circle-outline
                          </v-icon>
                          <span class="ml-1" style="margin-top: 1px">
                            {{ $t('message.blocked') }}
                          </span>
                        </span>
                      </template>
                      <base-input
                        v-model="video.title"
                        :placeholder="$t('message.addTitle')"
                        :label="$t('message.pages.videoPage.details.videoName')"
                        :rules="[
                          rules.required,
                          rules.maxLength,
                          rules.minLength,
                        ]"
                        @focus="inputBeingEdited('title')"
                        @blur="handleEdit"
                        :class="{
                          inputActive: 'title' === activeInput,
                        }"
                        :hide-details="
                          !('title' === activeInput) &&
                          !errors.failedToUpdateVideo
                        "
                        counter="255"
                        class="video-name-input"
                        required
                        color="primary"
                        outlined
                        ref="title"
                        @keyup.enter="handleEdit"
                        @keyup.escape="cancelEdit"
                      >
                        <template v-slot:append>
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-icon
                                v-bind="attrs"
                                v-on="on"
                                @click="cancelEdit"
                              >
                                mdi-close
                              </v-icon>
                            </template>
                            <span>
                              {{ $t('message.pages.video.cancel') }}
                            </span>
                          </v-tooltip>
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-icon
                                v-bind="attrs"
                                v-on="on"
                                @click="handleEdit"
                              >
                                mdi-check
                              </v-icon>
                            </template>
                            <span>
                              {{ $t('message.pages.video.save') }}
                            </span>
                          </v-tooltip>
                        </template>
                      </base-input>
                    </v-col>
                    <v-col
                      cols="12"
                      class="container-title-description pa-0 mt-6"
                      :class="{ hide: isLoading }"
                    >
                      <base-textarea
                        v-model="video.description"
                        :placeholder="
                          $t(
                            'message.pages.videoPage.details.videoDescPlaceholder'
                          )
                        "
                        :label="
                          $t('message.pages.videoPage.details.videoDescription')
                        "
                        outlined
                        dense
                        color="primary"
                        counter="5000"
                        auto-grow
                        @focus="inputBeingEdited('description')"
                        @blur="handleEdit"
                        :class="{
                          inputActive: 'description' === activeInput,
                        }"
                        :hide-details="
                          !('description' === activeInput) &&
                          !errors.failedToUpdateVideo
                        "
                        :rules="[rules.length(5000)]"
                        ref="description"
                        @keyup.enter="handleEdit"
                        @keyup.escape="cancelEdit"
                      >
                        <template v-slot:append>
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-icon
                                v-bind="attrs"
                                v-on="on"
                                @click="cancelEdit"
                              >
                                mdi-close
                              </v-icon>
                            </template>
                            <span>
                              {{ $t('message.pages.video.cancel') }}
                            </span>
                          </v-tooltip>
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-icon
                                v-bind="attrs"
                                v-on="on"
                                @click="handleEdit"
                              >
                                mdi-check
                              </v-icon>
                            </template>
                            <span>
                              {{ $t('message.pages.video.save') }}
                            </span>
                          </v-tooltip>
                        </template>
                      </base-textarea>
                    </v-col>
                  </v-row>
                  <v-row
                    no-gutters
                    :data-step="6"
                    :data-title="$t(introPath + 'info.title')"
                    :data-intro="$t(introPath + 'info.description')"
                  >
                    <v-col
                      cols="6"
                      class="align-center pa-0 mt-6"
                      :class="{ hide: PandaPlayerIsNotReady }"
                    >
                      <div class="d-flex flex-column justify-start align-start">
                        <span class="body-label-xs">{{
                          $t('message.pages.videoPage.details.lang')
                        }}</span>
                        <div
                          v-if="video.original_lang"
                          class="d-flex flex-row py-5"
                        >
                          <FlagCards
                            :items="[
                              (getLangCodeBySrcLang(video.original_lang) || {})
                                .country,
                            ]"
                          />
                          <span
                            class="body-regular-small details-font-color pl-2"
                            >{{
                              (getLangCodeBySrcLang(video.original_lang) || {})
                                .label
                            }}</span
                          >
                        </div>
                        <div v-else class="d-flex flex-row">
                          <span
                            class="body-regular-small details-font-color py-5"
                            >{{
                              $t(
                                'message.pages.videoPage.details.undefinedLang'
                              )
                            }}</span
                          >
                        </div>
                      </div>
                    </v-col>
                    <v-col
                      cols="6"
                      class="align-center pa-0 mt-6"
                      :class="{ hide: PandaPlayerIsNotReady }"
                    >
                      <div class="d-flex flex-column justify-start align-start">
                        <span class="body-label-xs">{{
                          $t('message.pages.videoPage.details.defaultCaption')
                        }}</span>
                        <div
                          v-if="
                            getLangLabelBySrclang(video.config.defaultSubtitle)
                          "
                          class="d-flex flex-row py-5"
                        >
                          <img
                            style="width: 16px; height: 16ox"
                            :src="currentIcon(video.config.defaultSubtitle)"
                          />

                          <span class="body-regular-small pl-2">{{
                            getLangLabelBySrclang(video.config.defaultSubtitle)
                          }}</span>
                        </div>
                        <div v-else class="d-flex flex-row">
                          <span
                            class="body-regular-small details-font-color py-5"
                            >{{
                              $t(
                                'message.pages.videoPage.details.noDefaultCaption'
                              )
                            }}</span
                          >
                        </div>
                      </div>
                    </v-col>
                    <v-col
                      cols="4"
                      class="align-center pa-0 mt-6"
                      :class="{ hide: PandaPlayerIsNotReady }"
                    >
                      <div class="d-flex flex-column justify-start align-start">
                        <span class="body-label-xs">{{
                          $t('message.pages.videoPage.details.size')
                        }}</span>
                        <div class="d-flex flex-row">
                          <span
                            class="body-regular-small details-font-color py-5"
                            >{{ infoPage.size }}</span
                          >
                        </div>
                      </div>
                    </v-col>
                    <v-col
                      cols="4"
                      class="align-center pa-0 mt-6"
                      :class="{ hide: PandaPlayerIsNotReady }"
                    >
                      <div class="d-flex flex-column justify-start align-start">
                        <span class="body-label-xs">{{
                          $t('message.pages.videoPage.details.createdAt')
                        }}</span>
                        <div class="d-flex flex-row">
                          <span
                            class="body-regular-small details-font-color py-5"
                            >{{ infoPage.createdAt }}</span
                          >
                        </div>
                      </div>
                    </v-col>
                    <v-col
                      cols="4"
                      class="align-center pa-0 mt-6"
                      :class="{ hide: PandaPlayerIsNotReady }"
                    >
                      <div class="d-flex flex-column justify-start align-start">
                        <span class="body-label-xs">{{
                          $t('message.pages.videoPage.details.modifiedOn')
                        }}</span>
                        <div class="d-flex flex-row">
                          <span
                            class="body-regular-small details-font-color py-5"
                            >{{ infoPage.modifiedOn }}</span
                          >
                        </div>
                      </div>
                    </v-col>
                  </v-row>
                  <v-row
                    no-gutters
                    :data-step="7"
                    :data-title="$t(introPath + 'metrics.title')"
                    :data-intro="$t(introPath + 'metrics.description')"
                  >
                    <v-col
                      cols="12"
                      class="align-center pa-0 mt-6"
                      :class="{ hide: PandaPlayerIsNotReady }"
                    >
                      <div class="d-flex flex-column justify-start align-start">
                        <span class="body-label-xs">{{
                          $t('message.pages.videoPage.details.analytics')
                        }}</span>
                        <div class="d-flex flex-row">
                          <!-- <span
                      class="body-regular-small details-font-color py-5"
                      >{{
                        $t('message.pages.videoPage.details.goToAnalytics')
                      }}</span
                    > -->
                          <a type="primary" @click="handleOpenConfirmMetrics">
                            <span class="body-bold-small py-5">
                              {{
                                $t(
                                  'message.pages.videoPage.details.goToAnalytics'
                                )
                              }}
                            </span>
                            <v-icon
                              class="body-bold-small"
                              color="primary"
                              size="12"
                              >mdi-open-in-new</v-icon
                            >
                          </a>
                        </div>
                      </div>
                    </v-col>
                  </v-row>
                </v-row>
              </v-container>
            </portal>
          </div>
        </div>
        <div class="grid__right-col">
          <div
            class="grid__settings"
            :class="{ 'grid__settings--no-footer': !showFooter }"
          >
            <v-row no-gutters>
              <v-col cols="12" class="py-10 px-6">
                <video-settings-loader v-if="loadingVideoSettings">
                </video-settings-loader>
                <video-player
                  v-else
                  :isExternalSourceVideo="isExternalSourceVideo"
                  :handlePlayerSelection="handleUpdateOldPlayerSelection"
                  :oldPlayerSelection="oldPlayerSelection"
                  :hasEdit="hasEdit"
                  @showFooter="(val) => (showFooter = val)"
                />
              </v-col>
            </v-row>
          </div>
          <div
            v-if="showFooter"
            class="grid__footer d-flex flex-column actions-column"
          >
            <div class="divider-div">
              <v-divider></v-divider>
            </div>
            <portal-target name="actions-footer" multiple></portal-target>
          </div>
        </div>
      </div>
      <!-- dialogs -->
      <details-mobile
        v-if="showDetails"
        :showDetails="showDetails"
        @closeDetailsModal="showDetails = false"
      ></details-mobile>
      <duplicate-video-modal
        :openModal="openDuplicateVideoModal"
        :videoId="videoId"
        :goToDuplicatedVideo="true"
        @close="openDuplicateVideoModal = false"
      />
      <embed-video
        v-if="showMenuEmbed"
        :startTime="startTime"
        :timeVideo="timeVideo"
        :videoId="videoId"
        :showMenuEmbed="showMenuEmbed"
        :embedSelected="embedSelected"
        :showMenuPosition="menuPosition"
        :fetching="isLoading"
        :video="video"
        :defaultTab="defaultTab"
        :targetClass="targetClass"
        :isExternalSourceVideo="isExternalSourceVideo"
        @start-time="startTime = $event"
        @showEmbed="(val) => (showMenuEmbed = val)"
        @playOpensFullscreen="handlePlayOpensFullscreen"
        @preloadEmbed="preloadEmbed = $event"
      />
      <v-menu
        min-width="250"
        close-on-click
        absolute
        offset-y
        :position-x="menuPosition.x"
        :position-y="menuPosition.y"
        :close-on-content-click="!bypassClickCloseMenu"
        :value="showMenu"
        @input="handleInput"
      >
        <v-list dense class="py-0 context-menu-list">
          <v-list-item v-if="isMobileBreakpoint" @click="showEmbed(1)">
            <v-list-item-icon class="mr-3">
              <v-icon>mdi-poll</v-icon>
            </v-list-item-icon>
            <v-list-item-title> Performance </v-list-item-title>
          </v-list-item>
          <v-list-item @click="handleOpenLogs()">
            <v-list-item-title>
              {{ $t('message.pages.historyModal.history') }}
            </v-list-item-title>
          </v-list-item>
          <v-list-item @click="handleMoveVideo()" v-if="hasEditVideoPerm">
            <v-list-item-title>
              {{ $t('message.move') }}
            </v-list-item-title>
          </v-list-item>
          <template v-if="!isExternalSourceVideo && hasEditVideoPerm">
            <v-list-item
              @click="openDuplicateVideoModal = true"
              v-if="video.status === 'CONVERTED'"
            >
              <v-list-item-title>{{
                $t('message.duplicate')
              }}</v-list-item-title>
            </v-list-item>
            <MenuItemDownload
              v-if="hasEditVideoPerm && !checkIfIsImport(video)"
              :video="video"
              :timeout="1000"
              :video-id="videoId"
              :fetch-on-mount="false"
              :value="showMenu"
              @click.native="bypassClickCloseMenu = true"
              @fetched="bypassClickCloseMenu = false"
              @submitVideoDownload="(url) => downloadVideoSubmit(url)"
              :downloadLoadingStatus="downloadLoadingStatus"
            />
          </template>
          <v-divider />
          <v-list-item @click="handleRemoveItem()" v-if="hasEditVideoPerm">
            <v-list-item-title class="remove" style="padding-top: 3px">
              {{ $t('message.pages.video.remove') }}
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
      <v-dialog v-model="confirmRedirect" max-width="550px">
        <v-card>
          <v-card-title class="text-h5">
            <v-icon class="pr-2" large color="primary">mdi-alert</v-icon>
            {{ $t('message.pages.video.pageAlert.title') }}
            <v-spacer></v-spacer>
            <v-btn icon @click="handleCloseConfirmRedirect">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text>
            {{
              $t(
                `message.pages.video.${
                  confirmRedirectWithChange
                    ? 'pageAlertWithChange'
                    : 'pageAlert'
                }.message`
              )
            }}
          </v-card-text>
          <v-card-text v-if="confirmRedirectWithChange">
            {{ $t('message.pages.video.pageAlertWithChange.confirmMessage') }}
          </v-card-text>
          <v-card-actions class="px-6 py-5">
            <v-spacer></v-spacer>
            <base-btn
              class="mr-4"
              type="secondary"
              @click="handleCloseConfirmRedirect"
            >
              {{
                $t(
                  `${
                    confirmRedirectWithChange
                      ? 'message.actions.return'
                      : 'message.close'
                  }`
                )
              }}
            </base-btn>
            <base-btn
              @click="
                confirmRedirect = false
                $router.push({
                  name: 'AnalyticsV2Account',
                  query: {
                    v: video.video_external_id,
                  },
                })
              "
            >
              {{
                confirmRedirectWithChange
                  ? $t('message.actions.discardChanges')
                  : 'Ok'
              }}
            </base-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <download-alert-dialog />
    </template>
  </page-view>
</template>

<script>
/* eslint-disable */
/* eslint-disable no-unused-vars */
/* eslint-disable no-unreachable */
/* eslint-disable vue/no-unused-components */
/* eslint-disable no-constant-condition */

// external imports
import { mapActions, mapMutations, mapState } from 'vuex'
import { mask } from 'vue-the-mask'
import { isBefore, subMinutes } from 'date-fns'
import _ from 'lodash'
import CountryFlag from 'vue-country-flag'

// internal imports
import { embedOptions as platformsOfEmbed } from '@/utils/embedOptions.js'
import i18n from '@/translations/lang'
import { ENV_CONFIG, BACKUP_DOWNLOADER_SERVER } from '@/config/envs'
import Storage from '@/utils/Storage'
import embedPageMixin from '@/utils/embedPage.mixin.js'
import embedSelection from '@/utils/embedSelection.mixin.js'
import introMixinVideoPage from '@/utils/introVideoPage.mixin.js'
import calcMegabytesMixin from '@/utils/calcMegabytes.js'
import mutedCustomMixin from '@/utils/mutedCustom.mixin.js'
import downloadUrlMixin from '@/utils/downloadUrl.mixin.js'
import externalSourceVideo from '@/utils/externalSourceVideo.mixin.js'
import langCodesMixin from '@/utils/langCodes.mixin.js'
import aiWorkflowMixin from '@/utils/aiWorkflow.mixin'
// API
import videosApi from '@/services/videosApi'
import drmApi from '@/services/drmApi'
import PandaAiApi from '@/services/aiworkflowApi'

// Components
import VideoInfo from './info/VideoInfo.vue'
import VideoPlayer from './player/VideoPlayer.vue'
import LoadingLogo from '@/components/LoadingLogo.vue'
import VideoMetrics from './metrics/VideoMetrics.vue'
import EmbedVideo from '@/components/EmbedVideo.vue'
import DetailsMobile from '@/components/VideoPage/VideoDetailsMobile.vue'
import referApi from '@/services/referApi.js'
import DuplicateVideoModal from '@/components/DuplicateVideoModal.vue'
import PageView from '@/layout/VideoPage/PageView_VideoPage.vue'
import MenuItemDownload from '@/components/MenuItemDownload.vue'
import DownloadAlertDialog from '@/components/DownloadAlertDialog.vue'
import FlagCards from '@/components/FlagCards.vue'
import BaseAlert from '@/components/BaseAlert.vue'
// skeleton loaders
import VideoSettingsLoader from '@/components/CustomLoaders/VideoPage/VideoSettingsLoader.vue'
import IframeLoader from '@/components/CustomLoaders/VideoPage/IframeLoader.vue'
import VideoDetailsLoader from '@/components/CustomLoaders/VideoPage/VideoDetailsLoader.vue'
// empty state
import VideoNotFound from '@/components/EmptyStateView/VideoPage/VideoNotFound.vue'
import VideoInTrash from '@/components/EmptyStateView/VideoPage/VideoInTrash.vue'
export default {
  components: {
    VideoInfo,
    VideoPlayer,
    LoadingLogo,
    VideoMetrics,
    EmbedVideo,
    DuplicateVideoModal,
    PageView,
    MenuItemDownload,
    CountryFlag,
    DownloadAlertDialog,
    BaseHeader: () => import('@/layout/VideoPage/BaseHeader_VideoPage.vue'),
    BaseBtn: () => import('@/components/BaseBtn.vue'),
    BaseInput: () => import('@/components/BaseComponents/BaseInput.vue'),
    BaseTextarea: () => import('@/components/BaseComponents/BaseTextarea.vue'),
    BaseChip: () => import('@/components/BaseComponents/BaseChip.vue'),
    BaseProgressCircular: () =>
      import('@/components/BaseComponents/BaseProgressCircular.vue'),
    FlagCards,
    VideoSettingsLoader,
    IframeLoader,
    VideoDetailsLoader,
    VideoNotFound,
    VideoInTrash,
    BaseAlert,
    DetailsMobile,
  },
  mixins: [
    aiWorkflowMixin,
    embedPageMixin,
    embedSelection,
    introMixinVideoPage,
    calcMegabytesMixin,
    mutedCustomMixin,
    externalSourceVideo,
    downloadUrlMixin,
    langCodesMixin,
  ],
  name: 'VideoPage',
  directives: { mask },
  data: () => ({
    introPath: 'message.pages.videoPage.introJS.',
    PandaPlayerIsNotReady: true,
    isExternalSourceVideo: false,
    openDuplicateVideoModal: null,
    showMenu: false,
    targetClass: '',
    userLibrary: {},
    showMenuEmbed: false,
    showMenuLink: false,
    showMenuPerformance: false,
    menuItem: {},
    embedVideo: {
      width: 720,
      height: 360,
      startTimeCheckbox: false,
      startTime: '',
    },
    startTime: '00:00:00',
    menuPosition: {
      x: 72,
      y: 62,
    },
    currentTime: 0,
    timeVideo: 0,
    fetching: false,
    isLoading: false,
    oldVideo: {},
    isBeta: false,
    video: {
      title: '',
      description: '',
      video_external_id: '',
      id: '',
    },
    videoId: '',
    rules: {
      required: (v) => (v && !!v) || i18n.t('message.requiredField'),
      maxLength: (v) =>
        (v && v.length <= 255) || i18n.t('message.maximumLengthVideo'),
      minLength: (v) => (v && v.length > 2) || i18n.t('message.minimumLength'),
      length: (len) => (v) =>
        (v || '').length <= len || i18n.t('message.maximumLengthVideoDesc'),
    },
    errors: {
      failedToUpdateVideo: false,
      videoDoesNotExist: false,
      videoInTrash: false,
    },
    networkErrorMsg: '',
    defaultTab: 2,
    unknownError: '',
    embedSelected: '',
    videoCustomSize: false,
    autoplay: false,
    isCopied: false,
    authToken: '',
    value: 0,
    activeInput: null,
    db: null,
    radioActive: 0,
    item: '',
    thumbnailUrl: null,
    date: '',
    downloadServer: '',
    dataBeingReplicated: false,
    playOpensFullscreen: false,
    playOpensFullscreenNative: false,
    drmOptionsIndex: 0,
    refreshVideoTimeout: null,
    introPageKey: 'video-page',
    confirmRedirect: false,
    bypassClickCloseMenu: false,
    qualityToBeDownloaded: null,
    unsavedChanges: true,
    embedIframe: null,
    oldPlayerSelection: 'options',
    hasEdit: [],
    confirmRedirectWithChange: false,
    downloadVideosUrl: '',
    downloadLoadingStatus: false,
    showFooter: false,
    flagMap: {
      br: require('@/assets/brazil.svg'),
      us: require('@/assets/english.svg'),
      it: require('@/assets/italy.svg'),
      es: require('@/assets/spanish.svg'),
      fr: require('@/assets/france.svg'),
      de: require('@/assets/germany.svg'),
      ru: require('@/assets/russia.svg'),
      nl: require('@/assets/netherlands.svg'),
      pl: require('@/assets/poland.svg'),
      in: require('@/assets/india.svg'),
      tr: require('@/assets/turkey.svg'),
      id: require('@/assets/indonesia.svg'),
      bg: require('@/assets/bulgaria.svg'),
      ro: require('@/assets/romania.svg'),
      cz: require('@/assets/czech-republic.svg'),
      gr: require('@/assets/greece.svg'),
      fi: require('@/assets/finland.svg'),
      hr: require('@/assets/croatia.svg'),
      sk: require('@/assets/slovakia.svg'),
      jp: require('@/assets/japan.svg'),
      ua: require('@/assets/ukraine.svg'),
      kr: require('@/assets/south-korea.svg'),
      se: require('@/assets/sweden.svg'),
      no: require('@/assets/norway.svg'),
      rs: require('@/assets/serbia.svg'),
      hu: require('@/assets/hungary.svg'),
      sa: require('@/assets/saudi-arabia.svg'),
      th: require('@/assets/thailand.svg'),
      vn: require('@/assets/vietnam.svg'),
      cn: require('@/assets/china.svg'),
      ke: require('@/assets/kenya.svg'),
      my: require('@/assets/malaysia.svg'),
      ur: require('@/assets/pakistan.svg'),
      pk: require('@/assets/pakistan.svg'),
    },
    loadingVideoSettings: true,
    isIframeLoading: true,
    videoInTrashData: null,
    showConvertingVideoTooltip: false,
    videoIndexDBData: null,
    showDetails: false,
  }),
  provide() {
    return {
      introPageKey: this.introPageKey,
      video: this.video,
      showEmbed: this.showEmbed,
    }
  },
  async created() {
    this.handlePlayerObserver()
    this.isBeta = location.search.match('beta')
    this.authToken = Storage.getItem('accessToken')
    if (!window.db) {
      this.$root.$emit('initDexie')
    }
    this.isLoading = true
    await this.readVideoInfo()
    if (this.video.drm_group_ids && this.video.drm_group_ids.length) {
      await this.signDrmToken()
    }
    this.isLoading = false
    Storage.setItem('width', this.embedVideo.width)
    Storage.setItem('height', this.embedVideo.height)
    let storageEmbedOption = Storage.getItem('embedType')
    let embedOptionsValue = this.embedOptions.map((el) => el.value)
    let storageIndex = embedOptionsValue.indexOf(storageEmbedOption)

    if (storageIndex !== -1) {
      this.embedSelected = this.embedOptions[storageIndex].value
    } else {
      this.embedSelected = this.embedOptions[0].value
    }
    Storage.setItem('embedType', this.embedSelected)
    let pullzoneValue = Storage.getItem(
      `pullzone-${this.$store.state.user.userInfo.sub}`
    )
    this.userPullzoneName = pullzoneValue
    this.handleEmbedSelection()
    this.fetching = false
  },
  async mounted() {
    await this.handleVideoSettingsData()
    localStorage.removeItem('firstAccess')
    this.$root.$on('loadingVideoSettings', (isLoading) => {
      this.loadingVideoSettings = isLoading
    })
    this.$root.$on('refreshVideoInfo', () => {
      this.readVideoInfo()
    })
    this.$root.$on('defineTimeVideo', (val) => {
      this.timeVideo = this.video.length = val
      this.loadingExternalDuration = false
      this.$root.$emit('updatedExternalTimeVideo')
    })
    this.$root.$on('updateEmbedPreviewChange', () => {
      this.unsavedChanges = true
    })
    this.$root.$on('openEmbedModal', (val) => {
      this.showEmbed(1, val)
    })
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState == 'visible') {
        if (
          (this.video.status === 'DRAFT' ||
            this.video.status === 'CONVERTING') &&
          this.$route.name === 'VideoPage'
        ) {
          this.refreshVideoTimeout = setInterval(this.getStatusVideo, 5000)
        }
      } else {
        clearInterval(this.refreshVideoTimeout)
      }
    })
    this.$root.$on(
      'changeEmbed',
      function () {
        this.embedSelected = Storage.getItem('embedType')
          ? Storage.getItem('embedType')
          : i18n.t('message.pages.videoEditModal.embedConfiguration.default')
      }.bind(this)
    )
    this.$root.$on('updateVideoInfo', (video) => (this.video = video))
    this.getUserLibrary()
    this.lessThanThirtyMinutesAgo()
    this.$root.$on('removedVideoInPage', async () => {
      await window.db.videos.delete(this.video.id)
      this.handleBackToListing()
    })
    window.addEventListener('message', (event) => {
      if (!this.profile.colors) return
      const { data } = event
      const { captionsColor, captionsBackgroundColor } = this.profile.colors

      if (data.message === 'panda_timeupdate') {
        this.currentTime = Math.round(data.currentTime)
      }
      if (data.message === 'panda_ready') {
        this.PandaPlayerIsNotReady = false
        const iframe = document.getElementById('panda-player').contentWindow
        iframe.postMessage(
          {
            type: 'colors',
            parameter: {
              colors: {
                primaryColor: this.profile.colors?.primaryColor,
                controlsColor: this.profile.colors?.controlsColor,
                captionsColor: captionsColor,
                captionsBackgroundColor: captionsBackgroundColor,
              },
            },
          },
          '*'
        )
      }
    })
    this.playOpensFullscreen = Storage.getItem('playOpensFullscreen') ?? false
    this.playOpensFullscreenNative =
      Storage.getItem('playOpensFullscreenNative') ?? false

    this.setIntroJS({ key: this.introPageKey })

    this.konva.file = null
    this.konva.anima = null
    this.konva.gif = null
    this.konva.animator = null

    this.konva.id =
      this.$store.state.video.profile?.vsl?.customMutedIndicatorId || null

    this.isBrowserBrave =
      (navigator.brave && (await navigator.brave.isBrave())) || false

    if (this.isBrowserBrave) this.konva.percent = 1
  },
  beforeDestroy() {
    clearInterval(this.refreshVideoTimeout)
    this.refreshVideoTimeout = null
    this.$root.$off('removedVideoInPage')
    this.$root.$off('openEmbedModal')
    this.UPDATE_VIDEO({})
    this.UPDATE_VIDEO_PROFILE({})
    this.UPDATE_PLAYER({})
    this.UPDATE_VIDEO_PROFILE_ID(null)
    this.UPDATE_PANDA_AI({ key: 'status_check_control', value: false })
    this.UPDATE_AI_WORKFLOW(null)
  },
  watch: {
    value() {
      if (this.hasEdit && this.hasEdit.length > 0)
        this.confirmRedirectWithChange =
          typeof this.hasEdit[0] === 'function'
            ? !this.hasEdit[0]()
            : !this.hasEdit[0]
      if (this.confirmRedirectWithChange) this.value = 0
    },
    controlsColor(value) {
      if (value.toString().match(/#[a-zA-Z0-9]{8}/)) {
        this.controlsColor = value.substr(0, 7)
      }
    },
    radioActive: function () {
      this.radioActive === 1 ? (this.embedSelected = 'general') : ''
    },
    currentTime: function () {
      this.startTime = this.formatTime(this.currentTime, 'startTime')
    },
    startTime: function () {
      let time = this.startTime.split(':')
      let seconds = +time[0] * 60 + +time[1]
      let input = this.startTime
      if (this.timeVideo >= 3600) {
        if (time.length === 2) {
          seconds = +time[0] * 60 + +time[1]
        } else if (time.length === 1) {
          seconds = +time[0]
        } else {
          seconds = +time[0] * 60 * 60 + +time[1] * 60 + +time[2]
        }
      } else {
        if (time.length === 1) {
          seconds = +time[0]
        }
      }
      if (seconds > this.timeVideo) {
        this.embedVideo.startTime = this.timeVideo
        this.startTime = this.formatTime(this.timeVideo, 'startTime')
      }
      this.embedVideo.startTime = seconds
    },
  },
  computed: {
    ...mapState({
      profile: (state) => state.video.profile,
      isMobileBreakpoint: (state) => state.user.isMobileBreakpoint,
    }),
    isConverting() {
      const status = this.video.status
      return ['CONVERTING', 'DRAFT'].includes(status)
    },
    isPlayable() {
      return this.video.playable
    },
    isExternalSourceNoLengthVideo() {
      if (this.isExternalSourceVideo) {
        if (!this.video.length) {
          return true
        }
      }
      return false
    },
    hasEditVideoPerm() {
      return (
        this.$store.state.user.userPermissions?.includes('put-video') ?? false
      )
    },
    isConvertingTooLazy() {
      if (
        Math.abs(new Date(this.video.updated_at) - new Date()) / 36e5 > 3 &&
        Math.abs(new Date(this.video.updated_at) - new Date()) / 36e5 < 48
      ) {
        return true
      }
      return false
    },
    textMetadata() {
      return `<link rel="prerender" href="https://player-${this.video.pullzone_name}.tv.pandavideo.com.br/embed/?v=${this.video.video_external_id}">
<link rel="preload" href="https://player-${this.video.pullzone_name}.tv.pandavideo.com.br/embed/js/hls.js" as="script">
<link rel="preload" href="https://player-${this.video.pullzone_name}.tv.pandavideo.com.br/embed/js/plyr.polyfilled.min.js" as="script">
<link rel="preload" href="https://config.tv.pandavideo.com.br/${this.video.pullzone_name}/${this.video.video_external_id}.json" as="fetch">
<link rel="preload" href="https://config.tv.pandavideo.com.br/${this.video.pullzone_name}/config.json" as="fetch">
<link rel="preload" href="https://b-${this.video.pullzone_name}.tv.pandavideo.com.br/${this.video.video_external_id}/playlist.m3u8" as="fetch">
<link rel="dns-prefetch" href="https://b-${this.video.pullzone_name}.tv.pandavideo.com.br">
<link rel="dns-prefetch" href="https://player-${this.video.pullzone_name}.tv.pandavideo.com.br">
<link rel="dns-prefetch" href="https://${this.video.pullzone_name}.b-cdn.net">`
    },
    embedOptions() {
      const [general] = platformsOfEmbed.filter(
        ({ value }) => value === 'general'
      )
      const rest = platformsOfEmbed.filter(({ value }) => value !== 'general')
      const sorted = rest.sort((a, b) => a.value.localeCompare(b.value))

      return [general, ...sorted]
    },
    pageTitle() {
      return this.$i18next.t('message.videos')
    },
    infoPage() {
      const videoInfo = {
        createdAt: '...',
        size: '...',
        modifiedOn: '...',
      }
      if (!this.video.id) return videoInfo
      const locale = this.getLocale()
      const created_at = new Date(this.video.created_at).toLocaleString(
        locale,
        { year: 'numeric', month: '2-digit', day: '2-digit' }
      )
      const updated_at = new Date(this.video.updated_at).toLocaleString(
        locale,
        { year: 'numeric', month: '2-digit', day: '2-digit' }
      )
      const size = this.addDecimal(
        this.calcMegabytes(this.video.storage_size, 'B', 'MB', true)
      )
      videoInfo.createdAt = created_at ?? '...'
      videoInfo.size = size ?? '...'
      videoInfo.modifiedOn = updated_at ?? '...'
      return videoInfo
    },
    isDomainFromBrazil() {
      return location.host.match(/.com$/) ? false : true
    },
    privacyPageLink() {
      return `https://pandavideo.com${
        this.isDomainFromBrazil ? '.br' : ''
      }/privacy/`
    },
  },
  methods: {
    ...mapActions('video', [
      'getVideoConfig',
      'getVideoProfileId',
      'getVideoPlayer',
    ]),
    ...mapMutations('video', [
      'UPDATE_VIDEO',
      'UPDATE_VIDEO_PROFILE',
      'UPDATE_PLAYER',
      'UPDATE_VIDEO_PROFILE_ID',
      'UPDATE_PANDA_AI',
      'UPDATE_AI_WORKFLOW',
    ]),
    ...mapMutations('user', ['UPDATE_GLOBAL_SNACKBAR']),
    async handleVideoSettingsData() {
      if (this.errors.isVideoInTrash || this.errors.videoDoesNotExist) return

      const playerConfig = this.$store.state.video.player?.config
      const videoId = this.$route.params.id
      this.videoIndexDBData = await this.getIndexDbVideoConfig(videoId)

      if (!this.videoIndexDBData.hasOwnProperty('aiWorkflow')) {
        await this.handlePandaAi(videoId)
      } else {
        const {
          aiWorkflow: { isEnabled },
        } = this.videoIndexDBData

        this.UPDATE_AI_WORKFLOW(isEnabled)
      }

      if (!playerConfig) await this.getVideoPlayer()
      this.loadingVideoSettings = false
    },
    async handlePandaAi(videoId) {
      const { data } = await PandaAiApi.getAiWorkflow(videoId)
      const isEnabled = this.checkPandaAiEnabling(data)
      await this.handlePandaAiStatus(isEnabled, videoId)
    },
    checkPandaAiEnabling(data) {
      let isEnabled

      if (!data.length) return (isEnabled = false)

      const pandaAiTypes = ['QUESTIONS', 'MINDMAP', 'ABSTRACT']
      const pandaAiData = data.filter((obj) => pandaAiTypes.includes(obj.type))

      if (!pandaAiData.length) return (isEnabled = false)
      const pandaAiStatus = ['QUEUE', 'RUNNING', 'FINISHED']

      isEnabled = pandaAiTypes.every((type) =>
        pandaAiData.some(
          (item) =>
            type.includes(item.type) && pandaAiStatus.includes(item.status)
        )
      )
      return isEnabled
    },
    async handlePandaAiStatus(isEnabled, videoId) {
      const obj = { aiWorkflow: { isEnabled: isEnabled } }
      Object.assign(this.videoIndexDBData, obj)
      await window.db.videos.update(videoId, this.videoIndexDBData)
      this.UPDATE_AI_WORKFLOW(isEnabled)
    },
    async getIndexDbVideoConfig(videoId) {
      const data = await window.db.videos.where({ id: videoId }).first()
      return data
    },
    handlePlayerObserver() {
      const _this = this
      const observer = new MutationObserver(function (mutationsList) {
        for (const mutation of mutationsList) {
          if (mutation.type === 'childList') {
            mutation.addedNodes.forEach((node) => {
              _this.findIframeElement(node)
            })
          }
        }
      })

      observer.observe(document, {
        childList: true,
        subtree: true,
      })
    },
    findIframeElement(node) {
      if (node.classList) {
        for (let i = 0; i < node.classList.length; i++) {
          if (node.classList[i] === 'iframe') {
            return (this.isIframeLoading = false)
          }
        }
      }
      if (node.children) {
        node.children.forEach((node) => {
          this.findIframeElement(node)
        })
      }
    },
    goToConversionArticle() {
      const articleLinks = {
        en: 'https://help.pandavideo.com/en-us/article/video-taking-too-long-to-convert-ouw4ho/',
        es: 'https://help.pandavideo.com/es/article/tu-video-tarda-demasiado-en-convertirse-s83y2g/',
        pt: 'https://help.pandavideo.com/pt-br/article/video-demorando-demais-para-converter-1uvumg4/',
      }
      const locale = this.$i18next.resolvedLanguage
      window.open(articleLinks[locale], '_blank')
    },
    checkIfIsImport(video) {
      if (!video.description) return
      const desc = video.description
      let val = desc.includes('hotmart')
      return val
    },
    async getUserLibrary() {
      try {
        let ret = await referApi.getLibrary()
        this.userLibrary = ret.data
      } catch (e) {
        console.log(e)
      }
    },
    handleGoToLink(link) {
      window.open(link, '_blank')
    },
    addDecimal(lengthString) {
      let numberPart = lengthString.split(' ')[0]
      let strPart = lengthString.split(' ')[1]

      numberPart = Number(numberPart).toFixed(1)
      return `${numberPart} ${strPart}`
    },
    getLocale() {
      if (this.$i18next.resolvedLanguage === 'en') return 'en-US'
      return 'pt-BR'
    },
    changeDrmIndex(value) {
      this.drmOptionsIndex = value
    },
    async signDrmToken() {
      try {
        let ret = await drmApi.signJwtToken(
          this.video.drm_group_ids[0].drm_group_id
        )
        this.signedToken = ret.data.jwt
        return
      } catch (e) {
        console.error(e)
        return
      }
    },
    async checkIfVideoIsInTrash(id) {
      const videosInTrash = await window.db.videos
        .where({ status: 'DELETING' })
        .toArray()

      const foundVideo = videosInTrash.find((video) => video.id === id)

      if (!!foundVideo) {
        this.videoInTrashData = foundVideo
      }
      return (this.errors.isVideoInTrash = !!foundVideo)
    },
    async readVideoInfo() {
      const videoId = this.$route.params.id
      await this.checkIfVideoIsInTrash(videoId)
      if (this.errors.isVideoInTrash) return
      try {
        let videoInfo = await videosApi.getAVideo(videoId)
        if (videoInfo.data.hasOwnProperty('video_source')) {
          this.isExternalSourceVideo = true
        }
        this.video = videoInfo.data
        this.oldVideo = { ...videoInfo.data }
        if (videoInfo.data.description === null) {
          this.video.description = ''
          this.oldVideo.description = ''
        }
        this.timeVideo = this.oldVideo.length
        this.videoId = videoInfo.data.id
        this.video.video_external_id = videoInfo.data.video_external_id
        if (
          this.video.status === 'DRAFT' ||
          this.video.status === 'CONVERTING'
        ) {
          this.refreshVideoTimeout = setInterval(this.getStatusVideo, 5000)
        }
        this.UPDATE_VIDEO(this.video)
        if (this.video.config.profile) {
          await this.getVideoProfileId(this.video.config.profile)
        }
      } catch (error) {
        this.errors.videoDoesNotExist = true
      }
    },
    async handleEdit() {
      this.errors.failedToUpdateVideo = false
      this.networkErrorMsg = ''
      this.activeInput = ''
      let videoObj = {}
      if (this.video.title !== this.oldVideo.title) {
        videoObj['title'] = this.video.title.trim()
      } else if (this.video.description !== this.oldVideo.description) {
        videoObj['description'] = this.video.description.trim()
      } else {
        this.triggerBlur()
        return
      }
      if (videoObj.description?.length > 5000) {
        this.errors.failedToUpdateVideo = true
        this.handleSnackbar('maxLength__description')
        return
      }
      if (videoObj.title?.length > 255) {
        this.errors.failedToUpdateVideo = true
        this.handleSnackbar('maxLength__title')
        return
      }
      if (videoObj.title?.length < 3) {
        this.errors.failedToUpdateVideo = true
        this.handleSnackbar('minLength__title')
        return
      }

      this.fetching = true
      try {
        const apiVideo = videoObj
        let ret = await videosApi.updateVideo({ ...apiVideo }, this.videoId)
        let dbUpdateObject = { title: ret.data.title }
        if (ret.data.description) {
          dbUpdateObject['description'] = ret.data.description
        }
        await window.db.videos.update(ret.data.id, dbUpdateObject)
        this.oldVideo = ret.data
        this.video.folder_id
          ? this.$root.$emit('folderEditVideo', ret.data)
          : this.$root.$emit('rootEditVideo', ret.data)
        this.handleSnackbar('changesSaved')
      } catch (e) {
        this.networkErrorMsg = e.response.data.errMsg
        this.errors.failedToUpdateVideo = true
      }
      this.triggerBlur()
      this.fetching = false
    },
    async handleDeleteVideo() {
      let videos = []
      videos.push(this.video)
      this.$root.$emit('deleteVideoModal', [...videos])
    },
    async getStatusVideo() {
      const { data: video } = await videosApi.getAVideo(this.$route.params.id)

      if (video.status !== 'DRAFT' && video.status !== 'CONVERTING') {
        clearInterval(this.refreshVideoTimeout)
        this.$root.$emit('updateVideoInListing', this.video)
        this.video.status = video.status
      }
    },
    handleSnackbar(param) {
      let text
      let type
      let timeout
      if (param === 'changesSaved') {
        text = i18n.t('message.changesSaved')
        type = 'success'
      }
      if (param === 'maxLength__description') {
        text = i18n.t('message.maximumLengthVideoDesc')
        type = 'error'
        timeout = 12000
      }
      if (param === 'maxLength__title') {
        text = i18n.t('message.maximumLengthVideo')
        type = 'error'
        timeout = 12000
      }
      if (param === 'minLength__title') {
        text = i18n.t('message.minimumLength')
        type = 'error'
        timeout = 12000
      }
      this.UPDATE_GLOBAL_SNACKBAR({
        open: true,
        type: type,
        text: text,
        timeout: timeout ?? 4000,
      })
    },
    lessThanThirtyMinutesAgo() {
      setTimeout(() => {
        this.lessThanThirtyMinutesAgo()
      }, 4000)
      this.dataBeingReplicated = isBefore(
        subMinutes(new Date(), 30),
        new Date(this.profile.updated_at)
      )
      return this.dataBeingReplicated
    },
    inputBeingEdited(i, ref) {
      this.activeInput = i
      if (ref) {
        this.activeInput = i + ref
      }
    },
    inputEndEdited() {
      this.activeInput = ''
    },
    cancelEdit() {
      this.inputEndEdited()
      if (this.video.title !== this.oldVideo.title) {
        this.video.title = this.oldVideo.title
      } else if (this.video.description !== this.oldVideo.description) {
        this.video.description = this.oldVideo.description
      }
      setTimeout(() => {
        document.activeElement.blur()
      }, 10)
    },
    triggerBlur() {
      document.activeElement.blur()
    },
    getRecuoModal(size) {
      const innerWindowWidth = window.innerWidth
      let recuo = innerWindowWidth - size

      if (recuo < 0) recuo = 0

      return recuo
    },
    showDots() {
      this.menuPosition.x = this.getRecuoModal(150)

      this.showMenu = false
      this.$nextTick(() => {
        this.showMenu = true
      })
    },
    showEmbed(tab, val = {}) {
      this.defaultTab = tab
      this.targetClass = val.target
      this.showMenuEmbed = false
      this.$nextTick(() => {
        this.showMenuEmbed = true
      })
    },
    showPerformance() {
      this.menuPosition.x = this.getRecuoModal(600)

      this.showMenuPerformance = false
      this.$nextTick(() => {
        this.showMenuPerformance = true
      })
    },
    showLink() {
      this.menuPosition.x = this.getRecuoModal(450)

      this.$nextTick(() => {
        this.showMenuLink = true
      })
    },
    formatTime(seconds, type) {
      const h = Math.floor(seconds / 3600)
      const m = Math.floor((seconds % 3600) / 60)
      const s = Math.round(seconds % 60)
      if (type) {
        0
        if (this.timeVideo >= 3600) {
          return [
            h > 9 ? h : h ? '0' + h : '0' + h || '00',
            m > 9 ? m : h ? '0' + m : '0' + m || '00',
            s > 9 ? s : '0' + s,
          ]
            .filter(Boolean)
            .join(':')
        } else {
          return [
            m > 9 ? m : h ? '0' + m : '0' + m || '00',
            s > 9 ? s : '0' + s,
          ]
            .filter(Boolean)
            .join(':')
        }
      }
      return [
        h > 9 ? h : h ? '0' + h : '0' + h || '00',
        m > 9 ? m : h ? '0' + m : '0' + m || '00',
        s > 9 ? s : '0' + s,
      ]
        .filter(Boolean)
        .join(':')
    },
    handleMoveVideo() {
      this.$root.$emit('moveModal', [this.$store.state.user.routedVideo])
    },
    handleRemoveItem() {
      this.$root.$emit('deleteVideoModal', [this.$store.state.user.routedVideo])
    },
    hasHistory() {
      return window.history.length > 2
    },
    handleBackToListing() {
      const videoFolderId = this.video.folder_id
      if (videoFolderId && videoFolderId != 'root') {
        this.$router
          .push({ name: 'Folders', params: { id: videoFolderId } })
          .catch(() => {})
        return
      }
      if (videoFolderId === 'root') {
        this.$router.push({ name: 'Videos' }).catch(() => {})
        return
      }
      if (this.hasHistory()) {
        this.$router.go(-1)
        return
      }
      this.$router.push({ name: 'Videos' }).catch(() => {})
    },
    handleCopyContent(value) {
      navigator.clipboard.writeText(`${value}`)
    },
    handlePlayOpensFullscreen({ script, native }) {
      this.playOpensFullscreen = script
      this.playOpensFullscreenNative = native
    },
    handleOpenLogs() {
      this.$root.$emit('openModalItemLogs', this.video)
    },
    currentIcon(defaultSubtitle) {
      const country = this.getCountryCodeBySrcLang(defaultSubtitle)
      const selectedItem = this.flagMap[country]

      return selectedItem ? selectedItem : require('@/assets/brazil.svg')
    },
    handleInput(event) {
      if (!this.bypassClickCloseMenu) {
        this.showMenu = event
      }
    },
    embedPage() {
      if (this.unsavedChanges) {
        this.embedIframe = this.embedPageGet()
        this.unsavedChanges = false
      }

      if (!this.embedIframe) {
        this.embedIframe = this.embedPageGet()
      }
      return this.embedIframe
    },
    handleUpdateOldPlayerSelection(option) {
      this.oldPlayerSelection = option
    },
    handleCloseConfirmRedirect() {
      this.confirmRedirect = false
      this.value = 0
      this.confirmRedirectWithChange = false
      this.handleAlterSelectVBtn(0, true)
      this.handleAlterSelectVBtn(1, false)
    },
    handleAlterSelectVBtn(index, activeButton) {
      this.$refs.buttonNavigation.$children[0].$children[index].classes[
        'v-btn--active'
      ] = activeButton
    },
    handleOpenConfirmMetrics() {
      this.confirmRedirect = true
      this.value = 1
    },
    downloadVideoSubmit(url) {
      this.downloadLoadingStatus = true
      this.downloadVideosUrl = url
      this.$nextTick(() => this.$refs.downloadVideosForm.submit())
      setTimeout(() => {
        this.downloadLoadingStatus = false
      }, 3000)
    },
  },
}
</script>

<style lang="scss" scoped>
.converting-alert-btn::v-deep {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-content: center;
  justify-content: center;
  span {
    white-space: nowrap;
  }
}
.converting-chip {
  height: 20px !important;
  background: varFigma(brand-90) !important;
  border-color: varFigma(brand-90) !important;
  border-radius: $spacing-05 !important;
  padding: 0 $spacing-03 !important;
  margin-left: 8px !important;
  &::v-deep .v-chip__content {
    color: varFigma(brand-20-darkest) !important;
    font-family: Rubik;
    font-size: 12px;
    font-style: normal;
    font-weight: 500;
    line-height: 150%;
  }
}

.grid--mobile {
  display: grid;
  grid-template-areas:
    'left-col'
    'right-col';
  grid-template-columns: 1fr;
  grid-template-rows:
    auto
    minmax(0, 1fr);
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;

  & .grid__left-col {
    grid-area: left-col;
    display: grid;
    grid-template-areas:
      'preview'
      'settings';
    grid-template-columns: 1fr;
    grid-template-rows:
      auto
      minmax(0, 1fr);
    height: 100%;
    overflow-x: hidden;
    overflow-y: hidden;
  }

  & .grid__preview {
    grid-area: preview;
    overflow-y: hidden;
    .v-sheet {
      overflow: hidden;
    }
  }

  .details--mobile {
    width: 100%;
    border-bottom: 1px solid varFigma(neutral-95-lightest);
    margin-top: 4px;
    margin-bottom: 4px;
    padding: $spacing-04 $spacing-05;
    .col {
      justify-items: center;
      display: flex;
      justify-content: center;

      button {
        width: 120px;
      }
    }
  }

  & .grid__details {
    display: none;
    grid-area: details;
    margin-top: $spacing-07;
    background-color: varFigma(neutral-100-white);
    border-radius: var(--spacing-02, 4px);
    border: 1px solid varFigma(neutral-95-lightest);
    overflow-y: auto;
  }

  & .grid__right-col {
    grid-area: right-col;
    display: grid;
    grid-template-areas: 'settings';
    grid-template-columns: 1fr;
    grid-template-rows: minmax(0, 1fr);
    height: 100%;
  }
  & .grid__settings {
    grid-area: settings;
    overflow-y: auto;
    overflow-x: hidden;
    margin-right: 2px;

    &--no-footer {
      padding-bottom: 80px;
    }
  }

  & .grid__footer::v-deep {
    & .vue-portal-target {
      .actions-row {
        padding: $spacing-04 $spacing-05;
        .spacer {
          display: none;
        }
        justify-content: center;
        div {
          width: 100%;
        }
        .base-button {
          width: 100%;
        }
      }
    }

    &.actions-column {
      .divider-div {
        padding: 0;
      }
    }
  }

  &__spacer {
    grid-area: spacer;
  }
}
.grid {
  @media (width > 768px) and (width < 1093px) {
    display: grid;
    grid-template-areas:
      'left-col'
      'right-col';
    grid-template-columns: 1fr;
    grid-template-rows: auto 1fr;
    height: 100%;
    overflow-x: hidden;
    overflow-y: auto;
    &__left-col {
      grid-area: left-col;
      grid-template-areas:
        'preview'
        'details';
    }
    &__right-col {
      grid-area: right-col;
      grid-template-areas: 'settings';
    }

    &__preview {
      grid-area: preview;
      overflow-y: hidden;
      .v-sheet {
        overflow: hidden;
      }
    }

    &__details {
      display: inherit;
      grid-area: details;
      margin-top: $spacing-07;
      background-color: varFigma(neutral-100-white);
      border-radius: var(--spacing-02, 4px);
      border: 1px solid varFigma(neutral-95-lightest);
      overflow-y: hidden;
    }
    &__settings {
      grid-area: settings;
      overflow-y: hidden;
      overflow-x: hidden;
      margin-right: 2px;

      &--no-footer {
        padding-bottom: 80px;
      }
    }

    &__footer::v-deep {
      & .vue-portal-target {
        .actions-row {
          padding: $spacing-07 0;
        }
        @media (width >= 1200px) {
          .actions-row {
            margin-right: 100px;
          }
        }
        @media (width < 1200px) {
          .actions-row {
            .spacer {
              display: none;
            }
            justify-content: center;
          }
        }
      }

      &.actions-column {
        .divider-div {
          padding: 0 $spacing-10;
        }
      }
    }

    &__spacer {
      grid-area: spacer;
    }
  }
  @media (min-width: 1093px) {
    display: grid;
    grid-template-columns: 50% auto;
    grid-template-rows: 100%;
    grid-template-areas: 'left-col right-col';
    height: 100%;

    &__left-col {
      grid-area: left-col;
      height: 100%;
      overflow-y: auto;
      display: grid;
      grid-template-areas:
        'preview'
        'details';
      grid-template-rows:
        auto
        1fr;
      padding: $spacing-10;

      & .grid__preview {
        grid-area: preview;
      }
      & .grid__details {
        grid-area: details;
        margin-top: $spacing-07;
        background-color: varFigma(neutral-100-white);
        border-radius: var(--spacing-02, 4px);
        border: 1px solid varFigma(neutral-95-lightest);
      }
    }

    &__right-col {
      grid-area: right-col;
      height: 100%;
      overflow-y: auto;
      display: flex;
      flex-direction: column;

      & .grid__settings {
        flex: 1 1 auto;
        overflow-y: auto;
        &--no-footer {
          padding-bottom: 80px;
        }
      }
    }
    &__footer::v-deep {
      grid-area: footer;
      flex: 0 0 auto;

      & .vue-portal-target {
        @media (width >= 1200px) {
          & .actions-row {
            margin-right: 100px;
          }
        }
        @media (width < 1200px) {
          & .actions-row {
            .spacer {
              display: none;
            }

            justify-content: center;
          }
        }
        & .actions-row {
          padding: $spacing-07 0;
        }
      }
      &.actions-column {
        .divider-div {
          padding: 0 $spacing-10;
        }
      }
    }
    &__spacer {
      grid-area: spacer;
    }
  }
}

.dots-row {
  width: 24px;
  height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  &.dots-active {
    border-radius: var(--spacing-02, 4px);
    background: var(--neutral-90, #e3e4e8);
  }
}

.container-title-description::v-deep {
  & .v-input__control .v-input__slot {
    padding: 0 !important;
    border: 1px solid varFigma(neutral-95-lightest);

    & .v-text-field__slot {
      padding: 0 12px !important;
      margin-right: 0 !important;
      border-radius: var(--spacing-02, 4px);
    }
    & .v-input__append-inner button {
      padding: 0 12px 0 0 !important;
    }
  }
  & .v-input__control .v-text-field__details {
    margin-bottom: 0 !important;
    padding-left: 0 !important;
    .v-counter {
      color: varFigma(neutral-40-light) !important;
      text-align: right;
      font-size: 12px;
      font-style: normal;
      font-weight: 400;
      line-height: 150%;
    }
  }
}

.details-font-color {
  color: varFigma(neutral-40-light);
}

.body-label-xs {
  color: varFigma(neutral-40-light);
  font-family: Rubik;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 100%;
}
</style>
<style lang="scss" scoped>
.hide {
  display: none;
}

.konva {
  &__container {
    position: relative;
  }

  &__stage {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1;

    width: 100%;
    height: 100%;
  }

  &__player {
    pointer-events: none;
  }
}

.external__player {
  position: relative;
  padding: 56.25% 0 0;
  width: 100%;
  height: 100%;
}

.context-menu-list::v-deep {
  .v-list-item__title {
    color: varFigma(neutral-30-base) !important;
    font-size: 12px !important;
    font-style: normal !important;
    font-weight: 400 !important;
    line-height: 150% !important;
    &.remove {
      color: varFigma(feedback-danger) !important;
    }
  }
}
</style>

<style>
.container-title-description .v-text-field--outlined fieldset,
.container-title-description .v-input__slot .v-input__append-inner {
  display: none;
}

.container-title-description .inputActive fieldset,
.inputActive .v-input__slot .v-input__append-inner,
.inputActive .v-textarea .v-input__append-inner {
  display: block !important;
}

.embed-textarea .v-text-field__slot {
  margin: 0 -47px 4px 0 !important;
}

.performance-textarea .v-text-field__slot {
  margin: 0 -47px 4px 0 !important;
}

.defaultThumb {
  height: 100%;
  width: 100%;
  object-fit: cover;
}

.embed-textarea .v-text-field__slot {
  margin: 0 -47px 4px 0 !important;
}

.icon-container {
  background-color: #eaedf6;
  padding: 4px;
  border-radius: 3px;
}

.wrapText {
  word-break: break-word;
}
.menuText {
  font-weight: 400;
  margin: 0 0 0 5px !important;
  letter-spacing: normal;
  font-family: 'Rubik', sans-serif !important;
  padding: 2px 0;
}
.bottom-navigation-playerConfig
  .v-item-group.v-bottom-navigation
  .v-btn.v-btn--active {
  border-bottom: 2px solid #6366f7;
}

.icon-accordion {
  order: 0;
}

.header-accordion {
  order: 1;
}

:root {
  --plyr-color-main: #fafafa;
  --plyr-captions-text-color: #fafafa;
  --plyr-captions-background: #fafafa;
  --plyr-video-control-color: #fafafa;
  --plyr-captions-text-color: #fafafa;
}

.button-choose-cta {
  width: 100%;
}

.button-choose-cta span {
  width: 100%;
  float: left;
  text-align: left;
  display: block;
}

.button-choose-cta span i {
  float: right;
}
@media (max-width: 1076px) {
  #update-changes {
    display: none;
  }
}
</style>
